'use client'; // this component could be split to keep RSC for a non-tooltip alert badge

import classNames from 'classnames';
import { ComponentProps } from 'react';
import { useIntl } from 'react-intl';
import StatusBadge from '@alltrails/denali/components/StatusBadge';
import Tooltip from '@alltrails/denali/components/Tooltip';
import Caution from '@alltrails/denali/icons/Caution';
import Closure from '@alltrails/denali/icons/Closure';
import Error from '@alltrails/denali/icons/Error';
import Info from '@alltrails/denali/icons/Info';
import { AlertType } from '@alltrails/types/Alert';
import { alertTypeLabel } from '../../utils/alertTypeLabel';
import styles from './AlertBadge.module.scss';

type AlertBadgeProps = {
  alertType: AlertType;
  label?: string;
  count?: number;
  tooltip?: boolean;
  onClick?: () => void;
  size: ComponentProps<typeof StatusBadge>['size'];
};

const alertTypeVariants: Record<AlertType, ComponentProps<typeof StatusBadge>['variant']> = {
  [AlertType.INFORMATION]: 'info',
  [AlertType.WARNING]: 'caution',
  [AlertType.CLOSURE]: 'negative',
  [AlertType.DANGER]: 'negative'
};

const alertTypeIcons: Record<AlertType, ComponentProps<typeof StatusBadge>['icon']> = {
  [AlertType.INFORMATION]: { Component: Info },
  [AlertType.WARNING]: { Component: Caution },
  [AlertType.CLOSURE]: { Component: Closure },
  [AlertType.DANGER]: { Component: Error }
};

const AlertBadge = ({ alertType, label, count, onClick, size, tooltip }: AlertBadgeProps) => {
  const intl = useIntl();
  const typeLabel = alertTypeLabel(alertType, intl);
  const defaultLabel = count && count > 1 ? `${typeLabel} • ${count}` : typeLabel;
  const text = label || defaultLabel;

  const badge = (
    <StatusBadge
      className={classNames(styles.badge, { [styles.clickable]: onClick })}
      variant={alertTypeVariants[alertType]}
      icon={alertTypeIcons[alertType]}
      text={text}
      onClick={onClick}
      size={size}
      treatment={alertType === AlertType.DANGER ? 'strong' : 'default'}
    />
  );

  return tooltip ? (
    <Tooltip
      containerClassName={styles.tooltipContainer}
      popperClassName={styles.tooltipPopper}
      anchorElement={badge}
      text={text}
      hidePointer
    />
  ) : (
    badge
  );
};

export default AlertBadge;
